import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Clock, Layers, DollarSign, ChevronRight } from "lucide-react";
import { COURSES } from "../data/staticData";
import Reveal from "../components/Reveal";
import SectionHeader from "../components/SectionHeader";
import PermitPackages from "../components/PermitPackages";
import JuniorPreparedness from "../components/JuniorPreparedness";
import YouthInActionVideo from "../components/YouthInActionVideo";
import JuniorGuardCard from "../components/JuniorGuardCard";

export default function Classes() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [location.hash]);

  return (
    <div data-testid="page-classes">
      <section className="container-fld py-20">
        <div className="overline mb-4">// Training Programs</div>
        <h1 className="font-tactical text-5xl md:text-7xl font-bold uppercase text-white tracking-tight leading-none max-w-4xl">
          Train Like It <span className="text-[#7CFF00]">Matters.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-zinc-400 text-lg leading-relaxed">
          Three core courses — engineered to take you from beginner to
          confident, responsible defender. All courses include hands-on range
          time and live-fire qualification.
        </p>
      </section>

      <PermitPackages />

      <section className="border-t border-white/10">
        {COURSES.map((c, i) => (
          <div
            id={c.id}
            key={c.id}
            data-testid={`course-section-${c.id}`}
            className={`border-b border-white/10 py-16 ${
              i % 2 === 1 ? "bg-[#0F0F0F]" : "bg-[#0A0A0A]"
            }`}
          >
            <div className="container-fld grid md:grid-cols-12 gap-10 items-start">
              <Reveal className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="relative aspect-[4/3] overflow-hidden corner-bracket p-2 group">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                </div>
              </Reveal>
              <Reveal delay={120} className="md:col-span-7">
                <div className="flex items-center gap-3 mb-3">
                  <i className={`${c.icon} text-[#7CFF00] text-xl`} />
                  <span className="overline">// Course 0{i + 1}</span>
                </div>
                <h2 className="font-tactical text-3xl md:text-5xl font-bold uppercase text-white tracking-tight leading-none">
                  {c.name}
                </h2>
                <p className="mt-4 text-zinc-300 text-lg leading-relaxed">
                  {c.description}
                </p>

                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="border border-white/10 p-3">
                    <Clock className="w-4 h-4 text-[#7CFF00] mb-1" />
                    <div className="overline text-[10px]">Duration</div>
                    <div className="font-tactical uppercase text-white text-sm tracking-wide mt-0.5">
                      {c.duration}
                    </div>
                  </div>
                  <div className="border border-white/10 p-3">
                    <Layers className="w-4 h-4 text-[#7CFF00] mb-1" />
                    <div className="overline text-[10px]">Skill Level</div>
                    <div className="font-tactical uppercase text-white text-sm tracking-wide mt-0.5">
                      {c.level}
                    </div>
                  </div>
                  <div className="border border-white/10 p-3">
                    <DollarSign className="w-4 h-4 text-[#7CFF00] mb-1" />
                    <div className="overline text-[10px]">Price</div>
                    <div className="font-tactical uppercase text-white text-sm tracking-wide mt-0.5">
                      {c.price}
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6 mt-8">
                  <div>
                    <h3 className="overline mb-3">What You'll Learn</h3>
                    <ul className="space-y-2">
                      {c.learn.map((l) => (
                        <li
                          key={l}
                          className="flex items-start gap-2 text-sm text-zinc-300"
                        >
                          <ChevronRight className="w-4 h-4 text-[#7CFF00] mt-0.5 flex-shrink-0" />
                          <span>{l}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="overline mb-3">Gear Required</h3>
                    <ul className="space-y-2">
                      {c.gear.map((g) => (
                        <li
                          key={g}
                          className="flex items-start gap-2 text-sm text-zinc-300"
                        >
                          <ChevronRight className="w-4 h-4 text-[#7CFF00] mt-0.5 flex-shrink-0" />
                          <span>{g}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8">
                  <Link
                    to={`/booking?course=${encodeURIComponent(c.name)}`}
                    data-testid={`reserve-${c.id}`}
                    className="btn-primary"
                  >
                    Reserve Your Spot <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        ))}
      </section>

      <JuniorPreparedness />
      <YouthInActionVideo />

      <section className="container-fld py-24">
        <div className="flex flex-col items-center gap-6 mb-10">
          <img
            src="/badges/uscca-certified-instructor.png"
            alt="USCCA Certified Instructor"
            className="h-32 md:h-40 w-auto drop-shadow-[0_0_24px_rgba(124,255,0,0.35)]"
          />
        </div>
        <SectionHeader
          overline="// Instructor"
          title="USCCA Certified. Veteran Tested."
          subtitle="Our lead instructor brings veteran-grade discipline, formal USCCA certification, and a calm, judgement-free teaching style — built for first-timers and experienced shooters alike."
          align="center"
        />
      </section>

      <JuniorGuardCard />
    </div>
  );
}
